import React, { useCallback, useState } from 'react';
import { Upload, FileText, CheckCircle2, AlertCircle, Loader2, X } from 'lucide-react';
import { cn } from '../../../shared/utils/cn';

export interface SimpleUploadedFile {
  file: File;
  status?: 'uploading' | 'success' | 'error';
  message?: string;
}

interface Props {
  value: SimpleUploadedFile | null;
  onSelect: (file: File) => void;
  onClear: () => void;
  disabled?: boolean;
  acceptedExtensions?: string[]; // e.g., ['.docx','.doc','.txt']
  className?: string;
}

export function FileUpload({
  value,
  onSelect,
  onClear,
  disabled = false,
  acceptedExtensions = ['.docx', '.doc', '.txt'],
  className,
}: Props) {
  const [isDragging, setIsDragging] = useState(false);

  const validate = useCallback(
    (file: File) => {
      const lower = file.name.toLowerCase();
      const ok = acceptedExtensions.some((ext) => lower.endsWith(ext));
      if (!ok) {
        alert(`Please upload only: ${acceptedExtensions.join(', ')}`);
        return false;
      }
      return true;
    },
    [acceptedExtensions]
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file && validate(file)) onSelect(file);
      e.target.value = '';
    },
    [onSelect, validate]
  );

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  }, [disabled]);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    const file = e.dataTransfer.files?.[0];
    if (file && validate(file)) onSelect(file);
  }, [disabled, onSelect, validate]);

  return (
    <div className={cn('w-full', className)}>
      <div
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={cn(
          'border rounded-xl p-8 bg-white transition-colors',
          isDragging ? 'border-[#291B4C] bg-[#291B4C]/5' : 'border-[#291B4C]/20',
          disabled && 'opacity-60 pointer-events-none'
        )}
      >
        {!value ? (
          <label className={cn('w-full border-2 border-dashed rounded-lg p-12 block text-center cursor-pointer',
            isDragging ? 'border-[#291B4C] bg-[#291B4C]/10' : 'border-[#291B4C]/20 hover:border-[#291B4C]/40 hover:bg-[#291B4C]/5')}
          >
            <input
              type="file"
              accept={acceptedExtensions.join(',')}
              onChange={handleFileSelect}
              className="hidden"
              disabled={disabled}
            />
            <Upload className="w-10 h-10 mx-auto mb-3 text-[#291B4C]/40" />
            <p className="text-sm font-medium text-[#291B4C]">
              {isDragging ? 'Drop your file here' : 'Click to upload file or drag & drop'}
            </p>
            <p className="text-xs text-[#291B4C]/60 mt-1">
              Accepted: {acceptedExtensions.join(', ')} (Max 100MB)
            </p>
          </label>
        ) : (
          <div className="flex items-center gap-4 p-4 bg-[#291B4C]/5 rounded-lg">
            {value.status === 'uploading' && (
              <Loader2 className="w-5 h-5 text-[#291B4C] animate-spin" />
            )}
            {value.status === 'success' && (
              <CheckCircle2 className="w-5 h-5 text-green-600" />
            )}
            {value.status === 'error' && (
              <AlertCircle className="w-5 h-5 text-red-600" />
            )}
            <FileText className="w-5 h-5 text-[#291B4C]" />
            <div className="flex-1">
              <p className="text-sm font-medium text-[#291B4C]">{value.file.name}</p>
              {value.message && (
                <p className={cn('text-xs', value.status === 'error' ? 'text-red-600' : 'text-[#291B4C]/60')}>
                  {value.message}
                </p>
              )}
            </div>
            {!disabled && (
              <button type="button" onClick={onClear} className="text-[#291B4C]/40 hover:text-[#291B4C]">
                <span className="sr-only">Remove</span> <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

